// src/pages/WhyPage.jsx

import React, { useState, useEffect } from 'react';
import NavBarHome from '../Layouts/NavBarHome';
import FooterHome from '../Layouts/FooterHome';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHexagonCheck } from '@fortawesome/pro-regular-svg-icons';

const styles = {
  container: {
    fontFamily: "'Helvetica Neue', Arial, sans-serif",
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  introSection: {
    padding: '150px 20px',
    backgroundColor: '#ff2300',
    textAlign: 'center',
  },
  introTitle: {
    fontSize: '60px',
    color: '#fff',
    marginBottom: '20px',
    fontWeight: '700',
    lineHeight: '1.1',
  },
  introText: {
    fontSize: '18px',
    fontWeight: '400',
    color: '#fff',
    lineHeight: '1.6',
    maxWidth: '800px',
    margin: '0 auto',
  },
  mainSection: {
    padding: '50px 20px',
    backgroundColor: '#f9f9f9',
    flex: '1',
  },
  section: {
    maxWidth: '900px',
    margin: '0 auto 60px auto',
    textAlign: 'center',
  },
  sectionText: {
    fontSize: '18px',
    color: '#333',
    lineHeight: '1.7',
    marginBottom: '20px',
    fontWeight: '450',
    whiteSpace: 'pre-wrap',
  },
  coreHeader: {
    textAlign: 'center',
    margin: '60px 0 40px',
  },
  coreTitle: {
    fontSize: '60px',
    margin: 0,
    fontWeight: '700',
    lineHeight: '1.1',
  },
  coreRed: {
    color: '#ff2300',
  },
  valueOrder: {
    fontSize: '16px',
    color: '#ff2300',
    fontWeight: '600',
    marginBottom: '4px',
  },
  valueTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#111',
    margin: '0 0 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  valueSub: {
    fontStyle: 'italic',
    fontSize: '14px',
    fontWeight: '400',
    color: '#666',
    marginBottom: '12px',
  },
  button: {
    marginTop: '20px',
    padding: '12px 28px',
    fontSize: '16px',
    fontWeight: '400',
    backgroundColor: '#ff2300',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  specialSection: {
    textAlign: 'center',
    padding: '80px 20px',
    backgroundColor: '#fff',
  },
  title: {
    fontSize: '48px',
    color: '#333',
    marginBottom: '40px',
    fontWeight: '700',
    textAlign: 'center',
  },
  logo: {
    display: 'block',
    margin: '0 auto 20px',
    width: '120px',
  },
};

const content = {
  es: {
    sections: [
      {
        title: 'Nuestra misión',
        text: (
          <>
            <strong>Empoderamos a los líderes</strong> con{' '}
            <strong>
              herramientas esenciales para desarrollar y retener al mejor
              talento
            </strong>
            ,<br />
            ayudamos a los que tienen un bajo rendimiento a tener éxito y{' '}
            <strong>minimizamos los riesgos de rotación</strong> —<br />
            impulsando <strong>
              un crecimiento organizacional sostenible
            </strong>{' '}
            y asegurando que las empresas prosperen de verdad.
          </>
        ),
      },
      {
        values: [
          {
            title: 'Personalizable',
            sub: '(adj.) Capaz de adaptarse a cada persona.',
            text: 'Cada persona es única. Por eso nuestras herramientas se ajustan a las necesidades reales de cada uno. Soluciones hechas a medida para un progreso real.',
          },
          {
            title: 'Performable',
            sub: '(adj.) Capaz de llevarse a la práctica.',
            text: 'Nada sirve si no puede aplicarse. Diseñamos herramientas que funcionan en el día a día. Para que las ideas se conviertan en acción y los planes en resultados.',
          },
          {
            title: 'Perfectible',
            sub: '(adj.) Capaz de mejorar continuamente.',
            text: 'Creemos que todo puede mejorar. Con el análisis adecuado, el conocimiento correcto y el soporte constante, cualquier persona puede alcanzar su siguiente nivel.',
          },
        ],
      },
      {
        title: 'Creando culturas que florecen',
        text: 'Liderazgo con propósito. Equipos con sentido. Resultados que se comparten.',
        cta: 'Conoce nuestra visión',
      },
    ],
  },
  en: {
    sections: [
      {
        title: 'Our Mission',
        text: (
          <>
            We <strong>empower leaders</strong> with essential{' '}
            <strong>tools to develop and retain top talent,</strong>
            <br />
            help underperformers succeed, and{' '}
            <strong>minimize turnover risks</strong>—<br />
            driving <strong>sustainable organizational growth</strong> and
            ensuring companies truly thrive.
          </>
        ),
      },
      {
        values: [
          {
            title: 'Personalizable',
            sub: '(adj.) Able to adapt to each person.',
            text: 'Every person is unique. That’s why our tools adjust to each individual’s real needs. Tailor-made solutions for genuine progress.',
          },
          {
            title: 'Performable',
            sub: '(adj.) Able to be put into practice.',
            text: 'Nothing is useful if it can’t be applied. We design tools that work day-to-day, so ideas become action and plans deliver results.',
          },
          {
            title: 'Perfectible',
            sub: '(adj.) Capable of continuous improvement.',
            text: 'We believe everything can improve. With the right analysis, correct knowledge, and constant support, anyone can reach their next level.',
          },
        ],
      },
      {
        title: 'Creating cultures that flourish',
        text: 'Purpose-driven leadership. Teams with meaning. Shared success.',
        cta: 'Discover our vision',
      },
    ],
  },
};

const coreLabels = {
  es: {
    title: 'Valores clave',
    order: ['Primero', 'Segundo', 'Tercero'],
  },
  en: {
    title: 'Core Values',
    order: ['First', 'Second', 'Third'],
  },
};

function WhyPage() {
  const [language, setLanguage] = useState(
    () => localStorage.getItem('language') || 'es'
  );
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const lang = content[language];
  const { title: cvTitle, order: cvOrder } = coreLabels[language];

  // Ajuste de tamaño de título en móvil para la misión
  const missionTitleStyle = {
    ...styles.coreTitle,
    fontSize: isMobile ? '32px' : styles.coreTitle.fontSize,
  };

  // Ajuste de tamaño de título en móvil para "Valores clave"
  const valuesTitleStyle = {
    ...styles.coreTitle,
    fontSize: isMobile ? '32px' : styles.coreTitle.fontSize,
  };

  return (
    <div style={styles.container}>
      <NavBarHome language={language} />

      {/* Introductorio */}
      <section style={styles.introSection}>
        <h1 style={styles.introTitle}>
          {language === 'es'
            ? 'Estamos aquí para ayudar a tu equipo a prosperar'
            : "We're in business to help your people thrive"}
        </h1>
        <p style={styles.introText}>
          {language === 'es'
            ? 'Queremos ayudarte a liderar mejor. Porque cuando las personas crecen, las empresas prosperan.'
            : 'We want to help you lead better. Because when people grow, businesses thrive.'}
        </p>
      </section>

      {/* Contenido principal */}
      <main style={styles.mainSection}>
        {lang.sections.map((section, idx) => (
          <section
            key={idx}
            style={{
              ...styles.section,
              ...(idx === 0 ? { marginBottom: '150px' } : {}),
            }}
          >
            {idx === 0 && (
              <>
                <hr
                  style={{
                    border: 'none',
                    height: '2px',
                    backgroundColor: '#ff2300',
                    margin: '60px 0',
                  }}
                />
                <div style={styles.coreHeader}>
                  <h2 style={missionTitleStyle}>
                    <span style={{ color: '#222', marginRight: '8px' }}>
                      {language === 'es' ? 'Nuestra' : 'Our'}
                    </span>
                    <span style={styles.coreRed}>
                      {language === 'es' ? 'Misión' : 'Mission'}
                    </span>
                  </h2>
                </div>
                <p style={styles.sectionText}>{section.text}</p>
                <hr
                  style={{
                    border: 'none',
                    height: '2px',
                    backgroundColor: '#ff2300',
                    margin: '60px 0',
                  }}
                />
              </>
            )}

            {section.values && (
              <>
                <div style={styles.coreHeader}>
                  <h2 style={valuesTitleStyle}>
                    <span style={styles.coreRed}>{cvTitle.split(' ')[0]}</span>{' '}
                    {cvTitle.split(' ').slice(1).join(' ')}
                  </h2>
                </div>

                {isMobile ? (
                  section.values.map((val, i) => (
                    <div
                      key={i}
                      style={{
                        textAlign: 'center',
                        margin: '0 auto 40px',
                      }}
                    >
                      <p style={styles.valueOrder}>{cvOrder[i]}</p>
                      <h3
                        style={{
                          ...styles.valueTitle,
                          justifyContent: 'center',
                          margin: '0 auto 8px',
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faHexagonCheck}
                          style={{ marginRight: '8px', color: '#ff2300' }}
                        />
                        {val.title}
                      </h3>
                      <p style={styles.valueSub}>{val.sub}</p>
                      <p style={styles.sectionText}>{val.text}</p>
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      rowGap: '10px',
                      columnGap: '60px',
                      textAlign: 'left',
                    }}
                  >
                    {section.values.map((val, i) => (
                      <h3 key={`t-${i}`} style={styles.valueTitle}>
                        <FontAwesomeIcon
                          icon={faHexagonCheck}
                          style={{ marginRight: '8px', color: '#ff2300' }}
                        />
                        {val.title}
                      </h3>
                    ))}
                    {section.values.map((val, i) => (
                      <p key={`s-${i}`} style={styles.valueSub}>
                        {val.sub}
                      </p>
                    ))}
                    {section.values.map((val, i) => (
                      <p key={`b-${i}`} style={styles.sectionText}>
                        {val.text}
                      </p>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        ))}
      </main>

      {/* Nueva sección final */}
      <section style={styles.specialSection}>
        <img
          src="/BetherLogoLuminousOrange.png"
          alt="Bether logo"
          style={styles.logo}
        />
        <h2 style={{ ...styles.title, marginBottom: '10px' }}>
          {language === 'es'
            ? 'Creando culturas que florecen'
            : 'Creating cultures that flourish'}
        </h2>
        <p style={styles.sectionText}>
          {language === 'es'
            ? 'Liderazgo con propósito. Equipos con sentido. Resultados que se comparten.'
            : 'Purposeful leadership. Meaningful teams. Shared success.'}
        </p>
      </section>

      <FooterHome language={language} setLanguage={setLanguage} />
    </div>
  );
}

export default WhyPage;
